import type { AnalysisState } from '@aibi/contracts';
import { cn, Tooltip, TooltipContent, TooltipTrigger } from '@aibi/ui';
import { TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { useServices } from '../../../app/services';
import { visualizationOptions } from '../../model/visualization-commands';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';

/**
 * Selector de tipo para la visualizacion seleccionada. Cambiar el tipo conserva la
 * intencion analitica; los tipos incompatibles se marcan con el motivo.
 */
export function VisualizationPicker({ card }: { readonly card: AnalysisState | null }) {
  const { index } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const { rendererRegistry, visualizationCatalog } = useServices();
  const options = useMemo(
    () => (card ? visualizationOptions(card, index, visualizationCatalog) : []),
    [card, index, visualizationCatalog],
  );

  if (card === null) {
    return (
      <p className="px-2 py-1 text-xs text-muted-foreground">
        Seleccione una visualizacion para cambiar su tipo.
      </p>
    );
  }

  return (
    <div role="radiogroup" aria-label="Tipo de visualizacion" className="grid grid-cols-5 gap-1">
      {options.map(({ descriptor, compatible, reasons }) => {
        const definition = rendererRegistry.resolve(descriptor.id);
        const Icon = definition.icon;
        const active = card.visualization.type === descriptor.id;
        const note = !definition.available ? 'Proximamente' : compatible ? null : reasons.join(' ');
        return (
          <Tooltip key={descriptor.id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={`${definition.label}${note === null ? '' : ` (${note})`}`}
                className={cn(
                  'relative flex aspect-square items-center justify-center rounded-md border border-transparent text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
                  active && 'border-primary/40 bg-primary/10 text-primary',
                  !definition.available && 'opacity-40',
                )}
                onClick={() => {
                  execute({
                    type: 'set-visualization-type',
                    cardId: card.id,
                    visualization: descriptor.id,
                  });
                }}
              >
                <Icon aria-hidden className="size-4" />
                {definition.available && !compatible && (
                  <TriangleAlert
                    aria-hidden
                    className="absolute right-0.5 top-0.5 size-2.5 text-warning"
                  />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">{definition.label}</p>
              {note !== null && <p className="opacity-80">{note}</p>}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
